import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from '../lib/cn';
/**
 * Inline, non-blocking status messaging (docs/009 "Feedback"). A toast is for
 * transient confirmation; an Alert stays on the page and explains state.
 *
 * Every string is a child — nothing user-facing is baked in (CLAUDE.md #8).
 */
export const alertVariants = cva('flex w-full items-start gap-3 rounded-card border p-4 text-sm text-primary', {
    variants: {
        variant: {
            info: 'bg-brand-subtle border-brand/40',
            success: 'bg-success-subtle border-success/40',
            warning: 'bg-warning-subtle border-warning/40',
            danger: 'bg-danger-subtle border-danger/40',
        },
    },
    defaultVariants: {
        variant: 'info',
    },
});
/** The leading icon takes the variant's accent so the alert reads at a glance. */
const alertIconVariants = cva('mt-0.5 flex size-5 shrink-0 items-center justify-center', {
    variants: {
        variant: {
            info: 'text-brand',
            success: 'text-success',
            warning: 'text-warning',
            danger: 'text-danger',
        },
    },
    defaultVariants: {
        variant: 'info',
    },
});
export const Alert = forwardRef(function Alert({ className, variant = 'info', icon, children, role, ...props }, ref) {
    // `danger` interrupts: assistive tech announces it immediately. The rest are
    // polite status updates (docs/009 "Accessibility").
    const resolvedRole = role ?? (variant === 'danger' ? 'alert' : 'status');
    return (_jsxs("div", { ref: ref, role: resolvedRole, className: cn(alertVariants({ variant }), className), ...props, children: [icon === undefined ? null : (_jsx("span", { "aria-hidden": "true", className: alertIconVariants({ variant }), children: icon })), _jsx("div", { className: "flex min-w-0 flex-1 flex-col gap-1", children: children })] }));
});
export const AlertTitle = forwardRef(function AlertTitle({ className, ...props }, ref) {
    return (_jsx("p", { ref: ref, className: cn('font-semibold text-primary text-balance', className), ...props }));
});
export const AlertDescription = forwardRef(function AlertDescription({ className, ...props }, ref) {
    return _jsx("p", { ref: ref, className: cn('text-sm text-muted text-pretty', className), ...props });
});
//# sourceMappingURL=alert.js.map